import { apiFetch } from '~/lib/api';
import type { EnglishAssessmentStatus } from '~/lib/englishAssessment';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export interface MeResponse {
  is_operator: boolean;
  email: string;
  domain_admin: boolean;
}

export interface FunnelResponse {
  range: '7d' | '30d' | 'all';
  totals: { applications: number; drafts: number; queued: number; sent: number; replied: number; interviews: number; rejected: number };
  rates: { reply_rate: number; interview_rate: number; gate_pass_rate: number };
  trend: Array<{ date: string; sent: number; replied: number }>;
}

/** Un conteo de flujo con su período anterior (null con «Todo»: no hay contra qué comparar). */
export interface PeriodCount {
  value: number;
  previous: number | null;
}

export type BusinessOrigin = 'direct' | 'partner' | 'coupon' | 'courtesy';
/** La regla `referralStatus` del servicio, la misma del panel de socios. */
export type PlanState = 'paying' | 'trial' | 'no_trial' | 'canceled' | 'courtesy';

/**
 * `GET /metrics/activity`: quién entra, qué hace y si los avisos lo traen de
 * vuelta. Espejo de `ActivityResponse` en `admin/activity.ts` del servicio.
 */
export type ActivationStepName =
  | 'returned'
  | 'has_cv'
  | 'jobs_browsed'
  | 'preferences_saved'
  | 'applied'
  | 'trial'
  | 'paid';

export interface ActivityResponse {
  range: '7d' | '30d' | 'all';
  since: string | null;
  generated_at: string;
  /** El día más viejo con seguimiento; null si todavía no hay filas. */
  tracking_since: string | null;
  kpis: { today: number; d7: number; d30: number; stickiness: number | null };
  trend: Array<{
    date: string;
    active: number;
    new: number;
    returning: number;
    free: number;
    trial: number;
    plan: number;
    actions: number;
  }>;
  pages: Array<{ page: string; people: number; views: number }>;
  actions: Array<{ action: string; people: number; count: number }>;
  activation: {
    size: number;
    steps: Array<{ step: ActivationStepName; reached: number; of: number; available: boolean }>;
    signed_in_no_membership: number | null;
  };
  notifications: Array<{
    tag: string;
    sent: number;
    people: number;
    returned_48h: number;
    via_link: number;
    acted: number;
  }>;
  /**
   * La app móvil. `installs_*` = primer día de una persona en la app (con
   * sesión) en esa plataforma; no son las descargas de la tienda, que se ven en
   * App Store Connect y Play Console. Opcional: un servicio viejo no lo manda.
   */
  apps?: {
    tracking_since: string | null;
    kpis: {
      users_total: number;
      users_ios: number;
      users_android: number;
      active_7d_ios: number;
      active_7d_android: number;
      /** Personas distintas (quien usó las dos plataformas cuenta una vez). */
      active_7d_total?: number;
      app_share_7d: number | null;
    };
    trend: Array<{
      date: string;
      installs_ios: number;
      installs_android: number;
      active_web: number;
      active_ios: number;
      active_android: number;
    }>;
    versions: Array<{ platform: 'ios' | 'android'; version: string; people: number }>;
  };
  /** true si alguna lectura llegó a su tope: lo recortado es lo más viejo. */
  truncated: boolean;
}

/**
 * `GET /metrics/business`: el negocio, sin datos demo ni cuentas del equipo.
 * Cómo se calcula cada cifra está en `job-apply-service/src/routes/api/job-apply/admin/business.ts`.
 */
export interface BusinessResponse {
  range: '7d' | '30d' | 'all';
  /** Inicio del rango (ISO, 00:00 de Bogotá = 05:00 UTC). Null con «Todo». */
  since: string | null;
  previous_since: string | null;
  generated_at: string;
  kpis: {
    registrations: PeriodCount;
    with_cv: PeriodCount;
    trials_started: PeriodCount & { in_trial_now: number };
    paying_now: { value: number };
    courtesies_active: { value: number };
    first_charges: PeriodCount;
    cancellations: PeriodCount & { paid: number; trial: number };
    abandoned_checkouts:
      | { available: true; value: number; previous: number | null; sessions: number; truncated: boolean }
      | { available: false };
    mrr: {
      usd: number;
      by_source: { stripe: number; list_price: number; offline: number };
      unpriced: number;
      paying: number;
    };
  };
  funnel: { registered: number; with_cv: number; trial: number; first_charge: number };
  origins: Array<{ origin: BusinessOrigin; registered: number; trial: number; paying: number }>;
  top_partners: Array<{ code: string; name: string | null; registered: number; trial: number; paying: number }>;
  partners: {
    active: number;
    pending: number;
    referrals_total: number;
    referrals_range: number;
    commissions_paid_usd: number;
    commissions_pending_usd: number;
    commissions_failed_usd: number;
  };
  agencies: {
    new_in_range: number;
    active: number;
    pending: number;
    open_positions: number;
    /** Paquetes de evaluaciones de inglés vendidos en el rango. Ausente en un backend viejo; null si falló. */
    english_assessments?: { revenue_usd: number; previous_revenue_usd: number; packs_sold: number; credits_sold: number } | null;
  };
  recent: Array<{
    id: string;
    created_at: string | null;
    email: string;
    origin: BusinessOrigin;
    /** El código del socio o el cupón, según el origen. */
    code: string | null;
    state: PlanState;
    has_cv: boolean;
  }>;
  /** Sólo los días con movimiento: el cliente rellena los ceros. */
  trend: Array<{ date: string; registrations: number; trials: number; first_charges: number }>;
}

export interface QualityResponse {
  range: '7d' | '30d';
  /** Rebote real: dirección inexistente o suprimida. */
  bounce_rate: number;
  /** Postulaciones que no salieron (gate, oferta cerrada), sobre las enviadas. */
  discard_rate: number;
  top_discard_reason: { reason: string; count: number } | null;
  gate_failure_rate: number;
  paused_users: Array<{ email: string; paused_reason: string; paused_at: string }>;
  token_cost: { total_input: number; total_output: number; est_usd: number };
}

export interface WorkersResponse {
  workers: Array<{
    name: string;
    health: 'ok' | 'stale' | 'error' | 'unknown';
    last_run_at: string | null;
    last_status: 'ok' | 'error' | null;
    duration_ms: number;
    items_processed: number;
    items_failed: number;
    last_error: string | null;
    stale_threshold_ms: number;
  }>;
}

export interface Member {
  _id: string;
  email: string;
  name?: string;
  status: 'ACTIVE' | 'PAUSED' | 'DISCONNECTED';
  role: 'USER' | 'OPERATOR';
  /** Cuándo creó la cuenta. */
  created_at?: string | null;
  /** Su chat en el agente «TBit Jobs» de la plataforma; null si aún no se sincronizó. */
  platform_url?: string | null;
  /** Su hoja pública (jobs.tbit.app/perfiles/<código>); null si es privada o todavía no existe. */
  public_profile_url?: string | null;
  plan: 'STARTER' | 'PRO' | 'PREMIUM' | null;
  expires_at: string | null;
  daily_limit: number;
  visa_types: Array<'h2b' | 'h2a_jo'>;
  last_sent_at: string | null;
  paused_reason: string | null;
  sent_today: number;
  sent_total: number;
  replied: number;
  interviews: number;
  /** Envío automático encendido. Con plan y apagado, no le sale nada a nadie. */
  auto_send?: boolean;
  /** Su hoja de vida primaria. Ausente en un servicio viejo. */
  cv?: MemberCV;
  access?: MemberAccess;
  /** Quién la refirió; null si llegó sola. Ausente en un servicio viejo. */
  referral?: MemberReferral | null;
}

/** `partner` null = el código no es de un socio (cupón viejo o socio borrado). */
export interface MemberReferral {
  code: string;
  source: 'link' | 'coupon' | null;
  referred_at: string | null;
  partner: { name: string; community_name: string | null; status: string } | null;
}

/** `score` null = todavía no se puntuó, que no es cero. */
export interface MemberCV {
  has_cv: boolean;
  score: number | null;
  scored_at: string | null;
}

/**
 * Los grupos de las tarjetas de Usuarios (`segmentQuery` en el servicio):
 * enviando (lo que el cron toma), en prueba, activos sin plan, y pausados o
 * desconectados. Cuentan toda la base de candidatos, no la página.
 */
export type MemberSegment = 'sending' | 'trial' | 'no_plan' | 'stopped';
export type MemberSummary = { total: number } & Record<MemberSegment, number>;

export interface MemberList {
  page: number;
  total: number;
  /** Ausentes en un servicio viejo: la página los calcula con `total`. */
  pages?: number;
  page_size?: number;
  summary?: MemberSummary;
  members: Member[];
}

/**
 * Dónde está la persona con el plan, con la regla del panel de socios
 * (`referralStatus` en el servicio). `never_tried` separa, dentro de
 * `no_trial`, a quien nunca empezó la prueba de quien la gastó sin pagar.
 */
export interface MemberAccess {
  state: PlanState;
  plan_active: boolean;
  trial_ends_at: string | null;
  expires_at: string | null;
  never_tried: boolean;
  /** Muestras que se mandó a su propio correo, y borradores que tiene. */
  demos_previewed: number;
  drafts: number;
}

export interface Posting {
  _id: string;
  title: string;
  company: string;
  location: string;
  /**
   * `jobbank` = Canadá (Job Bank, con LMIA). `eures_open` / `eures_codc` /
   * `eures` = Europa (EURES): abierta a candidatos de fuera de la UE / oficio del
   * catálogo de difícil cobertura de España / sólo con papeles UE.
   */
  source: 'static_csv' | 'dol_h2b' | 'dol_h2a' | 'manual' | 'jobbank' | 'eures_open' | 'eures_codc' | 'eures';
  /** HELD = de Canadá, viva pero fuera del envío hasta `JOBBANK_RELEASE=on`. */
  status: 'ACTIVE' | 'ARCHIVED' | 'HELD';
  case_number: string | null;
  assigned_count: number;
  application_count: number;
  gate_pass_rate: number | null;
  created_at: string;
}

export type AdminAgencyStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED';

export interface AdminAgency {
  _id: string;
  name: string;
  status: AdminAgencyStatus;
  contact_email: string;
  country: string | null;
  website: string | null;
  created_by_email: string;
  created_at: string;
  access_requested_at: string | null;
  approved_at: string | null;
  member_count: number;
  open_position_count: number;
  /** Saldo de evaluaciones de inglés (caché del libro). Null = nunca tuvo. Ausente en un backend viejo. */
  assessment_credits_balance?: number | null;
}

/** Un asiento del libro de créditos de una agencia (vista de operador). */
export interface AdminCreditEntry {
  _id: string;
  type: string;
  credits: number;
  at: string;
  applied: boolean;
  settled_at: string | null;
  assessment_id: string | null;
  pack: string | null;
  amount_cents: number | null;
  stripe_session_id: string | null;
  note: string | null;
  by_email: string | null;
}

/** `GET /agency-admin/agencies/:id/credits`. */
export interface AdminAgencyCredits {
  agency: { _id: string; name: string; stripe_customer: boolean };
  balance: number;
  ledger_sum: number;
  in_sync: boolean;
  reserved: number;
  free_granted_at: string | null;
  entries: AdminCreditEntry[];
}

export type AdminPartnerStatus = 'PENDING' | 'ACTIVE' | 'PAUSED' | 'REJECTED' | 'TERMINATED';

/** Una fila de la bandeja del Programa de socios (`GET /api/admin/partners`). */
export interface AdminPartner {
  id: string;
  status: AdminPartnerStatus;
  name: string;
  email: string;
  whatsapp: string;
  country: string;
  community_name: string | null;
  promotion_plan: string;
  code: string;
  rejection_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  /** Sólo con TERMINATED. Ausentes en un backend anterior al 2026-10-08. */
  terminated_at?: string | null;
  termination_reason?: string | null;
  /** Retención de pagos: mientras exista, no sale ninguna transferencia al socio. */
  payouts_hold?: { reason: string; at: string | null; by: string | null } | null;
  /** La versión de los Términos del Programa que lo rige. */
  terms_version?: string | null;
  created_at: string | null;
  payouts: 'none' | 'pending' | 'ready';
  referrals: number;
  commissions: { pending_cents: number; paid_cents: number; failed_cents: number };
}

/**
 * Por qué se concede una cortesía. `paid_offline` (pagó por transferencia,
 * efectivo…) exige `amount_usd`: es plata que entró y tiene que quedar anotada.
 */
export const COMP_REASONS = ['paid_offline', 'courtesy', 'compensation'] as const;
export type CompReason = (typeof COMP_REASONS)[number];

// La operación del candidato conserva su montaje histórico. La administración
// de agencias pertenece al servicio extraído y usa `/agency-admin` directamente.
const BASE = '/job-apply/api/admin';

export const adminApi = {
  me: () => request<MeResponse>(`${BASE}/me`),
  funnel: (range: '7d' | '30d' | 'all' = '7d') => request<FunnelResponse>(`${BASE}/metrics/funnel?range=${range}`),
  business: (range: '7d' | '30d' | 'all' = '7d') => request<BusinessResponse>(`${BASE}/metrics/business?range=${range}`),
  activity: (range: '7d' | '30d' | 'all' = '7d') => request<ActivityResponse>(`${BASE}/metrics/activity?range=${range}`),
  quality: (range: '7d' | '30d' = '7d') => request<QualityResponse>(`${BASE}/quality?range=${range}`),
  workers: () => request<WorkersResponse>(`${BASE}/workers`),

  listMembers: (params: URLSearchParams) => request<MemberList>(`${BASE}/members?${params}`),
  getMember: (id: string) => request<{ member: any; recent_applications: any[] }>(`${BASE}/members/${id}`),
  patchMember: (id: string, body: { status?: string; daily_limit?: number; paused_reason?: string; auto_send?: boolean }) =>
    request(`${BASE}/members/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setRole: (id: string, role: 'USER' | 'OPERATOR') =>
    request(`${BASE}/members/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
  comp: (
    id: string,
    body: { plan: string; months: number; reason: CompReason; amount_usd?: number; note?: string },
  ) => request(`${BASE}/members/${id}/comp`, { method: 'POST', body: JSON.stringify(body) }),
  setPeriod: (id: string, body: { started_at?: string | null; expires_at?: string | null }) =>
    request(`${BASE}/members/${id}/period`, { method: 'PATCH', body: JSON.stringify(body) }),
  checkoutLink: (id: string, plan: string) =>
    request<{ url: string }>(`${BASE}/members/${id}/checkout-link`, { method: 'POST', body: JSON.stringify({ plan }) }),
  /** «Ver como»: deja constancia en el servidor y devuelve a quién se va a mirar. */
  viewAs: (id: string) =>
    request<{ member: { _id: string; email: string; name: string | null } }>(`${BASE}/members/${id}/view-as`, { method: 'POST' }),
  createOperator: (email: string) =>
    request(`${BASE}/operators`, { method: 'POST', body: JSON.stringify({ email }) }),

  listPostings: (params: URLSearchParams) => request<{ page: number; total: number; postings: Posting[] }>(`${BASE}/postings?${params}`),
  getPosting: (id: string) => request<{ posting: any; recent_applications: any[] }>(`${BASE}/postings/${id}`),
  patchPosting: (id: string, body: { status?: string; assigned_members?: string[] }) =>
    request(`${BASE}/postings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  syncDol: () => request(`${BASE}/sync-dol`, { method: 'POST' }),

  listAgencies: (params: URLSearchParams) =>
    request<{ page: number; total: number; agencies: AdminAgency[] }>(`/agency-admin/agencies?${params}`),
  approveAgency: (id: string) =>
    request<AdminAgency>(`/agency-admin/agencies/${id}/approve`, { method: 'POST' }),
  suspendAgency: (id: string) =>
    request<AdminAgency>(`/agency-admin/agencies/${id}/suspend`, { method: 'POST' }),
  reactivateAgency: (id: string) =>
    request<AdminAgency>(`/agency-admin/agencies/${id}/reactivate`, { method: 'POST' }),
  agencyCredits: (id: string) => request<AdminAgencyCredits>(`/agency-admin/agencies/${id}/credits`),
  /** `opId`: el mismo ajuste reintentado (respuesta perdida) no se aplica dos veces. */
  adjustAgencyCredits: (id: string, credits: number, note: string, opId: string) =>
    request<AdminAgencyCredits>(`/agency-admin/agencies/${id}/credits/adjust`, {
      method: 'POST',
      body: JSON.stringify({ credits, note, op_id: opId }),
    }),

  // Programa de socios. Aprobar no lleva monto: la economía es fija del programa.
  listPartners: (status?: string) =>
    request<{ partners: AdminPartner[] }>(`${BASE}/partners${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  approvePartner: (id: string) => request(`${BASE}/partners/${id}/approve`, { method: 'POST' }),
  rejectPartner: (id: string, reason: string) =>
    request(`${BASE}/partners/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  pausePartner: (id: string) => request(`${BASE}/partners/${id}/pause`, { method: 'POST' }),
  reactivatePartner: (id: string) => request(`${BASE}/partners/${id}/reactivate`, { method: 'POST' }),
  /** Definitivo. Lo causado se le sigue pagando (salvo retención). */
  terminatePartner: (id: string, reason: string) =>
    request(`${BASE}/partners/${id}/terminate`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** Frena toda transferencia al socio, en cualquier estado, hasta soltarla. */
  holdPartnerPayouts: (id: string, reason: string) =>
    request<{ partner: { id: string; payouts_hold: unknown }; in_flight?: number }>(
      `${BASE}/partners/${id}/hold-payouts`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  releasePartnerPayouts: (id: string) => request(`${BASE}/partners/${id}/release-payouts`, { method: 'POST' }),
};

// ─── Evaluación de inglés por llamada ───────────────────────────────────────

export interface AdminEnglishRow {
  _id: string;
  status: EnglishAssessmentStatus;
  agency: string | null;
  posting: string | null;
  candidate: string | null;
  /** Enmascarado por el servicio: '+57 •••• 4567'. */
  phone: string | null;
  calls: number;
  cefr: string | null;
  insufficient_evidence: boolean | null;
  result_source: 'reused' | 'shared_call' | null;
  cost: { telephony_usd?: number; ai_usd?: number; scoring_usd?: number } | null;
  demo: boolean;
  created_at: string;
  expires_at: string;
}

export interface AdminEnglishResponse {
  counts: Record<string, number>;
  cost: { telephony_usd: number; ai_usd: number; scoring_usd: number };
  recent: AdminEnglishRow[];
}

export const adminEnglishApi = {
  list: () => request<AdminEnglishResponse>('/job-apply/api/admin/english-assessments'),
  rescore: (id: string) =>
    request<{ ok: boolean; status: string }>(`/job-apply/api/admin/english-assessments/${id}/rescore`, { method: 'POST' }),
};
